import {ElementHelpers} from '../helpers/element-helpers';
import {Ajax} from '../helpers/ajax';
import {Templates} from '../helpers/templates';
import {Keys} from '../helpers/keys';

const template = `
<ul class="autosuggest__list input-combobox__list">
	{{#suggestions}}
	<li data-title="{{title}}" class="autosuggest__item input-combobox__list--item {{#selected}}selected{{/selected}}">{{title}}</li>
	{{/suggestions}}
	{{#showMore}}
	<li class="autosuggest__item input-combobox__list--item moresuggest">&hellip;</li>
	{{/showMore}}
</ul>
`;

export class Autosuggest {
	constructor(elem) {
		// only works on inputs
		if (elem.tagName.toLowerCase() === 'input') {
			this.elem = elem;
			this.suggestionsElem = this.elem.parentNode.querySelector('[data-autosuggest-container]');
			this.suggestionsListElements = this.suggestionsElem.querySelectorAll('li');
			this.url = this.elem.dataset.autosuggestUrl;
			this.searchParam = this.elem.dataset.autosuggestSearchparam;
			this.threshold = Autosuggest.getNumber(this.elem.dataset.autosuggestThreshold, 3);
			this.maxSuggestions = Autosuggest.getNumber(this.elem.dataset.autosuggestMax, 5);
			this.typingDelay = 175;
			this.typingTimeout = 0;
			this.suggestions = {};
			this.selectedItem = null;
			this.selectedIndex = 0;

			this.addEventHandlers();
		}
	}

	/**
	 * Parse number value or returns default
	 *
	 * @param value
	 * @param defaultsTo
	 * @returns {Number}
	 */
	static getNumber(value, defaultsTo) {
		return isNaN(value) ? defaultsTo : parseInt(value, 10);
	}

	static getSelector() {
		return '.autosuggest';
	}

	static transformToObject(array) {
		const obj = {'items': []};

		for (let i = 0; i < array.length; i++) {
			obj.items.push({'title': array[i]});
		}

		return obj;
	}

	get isWithinThreshold() {
		return (this.elem.value.length < this.threshold);
	}

	addEventHandlers() {
		this.suggestionsElem.addEventListener('click', (e) => {
			if (e.target.tagName === 'LI' && !e.target.classList.contains('moresuggest')) {
				this.selectedItem = e.target;
				this.useSelectedValue();
				ElementHelpers.triggerEvent(this.elem, 'change');
			}
		});

		this.elem.addEventListener('keydown', e => {

			switch (e.which) {
				case Keys.TAB: // Tab
					this.useSelectedValue();
					break;
				case Keys.ENTER: // Enter
					if (this.suggestionsListShown() && this.useSelectedValue()) {
						e.preventDefault();
						ElementHelpers.triggerEvent(this.elem, 'change');
					}
					break;
				case Keys.ESC: // Escape
					this.hideSuggestions();
					break;
				case Keys.UP: // Up
					e.preventDefault();
					this.selectItem(-1);
					break;
				case Keys.DOWN: // Down
					e.preventDefault();
					this.selectItem(1);
					break;
			}
		});

		this.elem.addEventListener('keyup', (e) => {
			if (Autosuggest.isCharacterKeycode(e.which)) {
				this.showSuggestions();
			} else if (this.isWithinThreshold) {
				this.hideSuggestions();
			}
		});

		this.elem.addEventListener('blur', (e) => {
			e.preventDefault();
		});

		this.elem.addEventListener('focus', (e) => {
			e.preventDefault();
		});

		document.addEventListener('click', (e) => {
			if (!this.suggestionsElem.classList.contains('hidden')) {

				if (!ElementHelpers.hasParentWithClass(e.target, 'autosuggest')) {
					this.hideSuggestions();
				}
			}
		});

	}

	createList() {
		const suggestions = this.suggestions.items;

		let suggestionData = {
			suggestions: []
		};

		for (let i = 0; i < this.maxSuggestions && i < suggestions.length; i++) {
			let suggestion = suggestions[i];
			if (i === 0) {
				suggestion.selected = true;
			}
			suggestionData.suggestions.push(suggestion);
		}


		if (suggestions.length > this.maxSuggestions) {
			suggestionData.showMore = true;
		}

		Templates.renderTemplate(template, this.suggestionsElem, suggestionData);
		this.suggestionsListElements = this.suggestionsElem.querySelectorAll('li');
	}

	fetchData(query) {
		let url = this.url;

		if (query && this.searchParam) {
			url += `${url.indexOf('?') > -1 ? '&' : '?'}${this.searchParam}=${query}`;
		}

		Ajax.GET(url).then((data) => {
			if (Array.isArray(data)) {
				if (data.length > 0) {
					this.suggestions = Autosuggest.transformToObject(data);
				} else {
					this.suggestions = undefined;
				}
			} else {
				this.suggestions = data;
			}

			if (this.suggestions) {

				this.createList();

				this.suggestionsElem.classList.toggle('hidden', !this.suggestionsElem.childNodes.length || this.isWithinThreshold);
				this.selectedIndex = 0;
			}
		});
	}

	showSuggestions() {
		const value = this.elem.value;

		if (!this.isWithinThreshold) {
			if (!this.suggestions) {
				this.fetchData(value);
			} else {
				clearTimeout(this.typingTimeout);

				this.typingTimeout = window.setTimeout(() => {
					this.fetchData(value);
				}, this.typingDelay);
			}
		}

	}

	hideSuggestions() {
		this.suggestionsElem.innerHTML = '';
		this.suggestionsElem.classList.add('hidden');

		this.suggestions = {};
		this.selectedIndex = 0;
		this.selectedItem = null;
	}

	suggestionsListShown() {
		return !!this.suggestions.items;
	}

	selectItem(num) {
		if (this.suggestions && this.suggestions.items && this.suggestions.items.length && this.suggestionsListElements[this.selectedIndex + num]) {
			this.suggestionsListElements[this.selectedIndex].classList.remove('selected');
			this.selectedIndex += num;
			this.suggestionsListElements[this.selectedIndex].classList.add('selected');
			this.selectedItem = this.suggestionsListElements[this.selectedIndex];
		}
	}

	useSelectedValue() {
		let hasSelected = true;
		if (this.suggestions && this.suggestions.items && this.suggestions.items.length && this.selectedItem !== null) {
			this.elem.value = this.selectedItem.dataset.title;
		} else if (this.selectedIndex === 0) {
			this.selectItem(0);
			this.elem.value = this.selectedItem.dataset.title;
		} else {
			hasSelected = false;
		}

		this.hideSuggestions();

		return hasSelected;
	}

	static isCharacterKeycode(keyCode) {
		return keyCode === 8 || keyCode > 40 || keyCode === 0;
	}

}
